import { useFspState, useSettlementCurrency } from "@fero/hooks";
import { TableCell, TableRow } from "@mui/material";
import type { StockDataCounts, StockListing } from "adl-gen/ferovinum/app/api";
import { PaginatedTable } from "components/widgets/paginated-table/paginated-table";
import { type FC, useCallback, useMemo, useState } from "react";
import { isSingles } from "utils/model-utils";
import {
  buildFspValuesMap,
  extractDealLegs,
  makeStockRowData,
} from "./helpers";
import { StockListingTableBody } from "./stock-listing-table-body";

// TODO: artfuldev: This is updated several times by several components. Revisit
export interface StockDataDisplayInfo extends StockDataCounts {
  stockDataMap: Map<string, StockListing[]>;
}

interface Props {
  loadMoreStockDataMap: (
    offset: number,
  ) => Promise<Map<string, StockListing[]> | undefined>;
  initStockListings: StockDataDisplayInfo;
}

export const StockListingTable: FC<Props> = ({
  loadMoreStockDataMap,
  initStockListings,
}) => {
  const initDealLegs = useMemo(() => {
    return extractDealLegs(initStockListings.stockDataMap);
  }, [initStockListings]);

  const { fspValues, fetchFspForDealLegs } = useFspState(
    initDealLegs.map((dl) => dl.dealLegWithDbId.id),
  );
  const [stockDataMap, setStockDataMap] = useState(
    initStockListings.stockDataMap,
  );
  const currency = useSettlementCurrency();

  // we need to build a map of fsp values for each product code, so that we can display the fsp range for
  // each product. This value is updated whenever we page in more data.
  const fspValuesMap = useMemo(() => {
    return buildFspValuesMap(stockDataMap, fspValues);
  }, [stockDataMap, fspValues]);

  /// We only display the quantity column for singles
  const displayQuantityColumn: boolean = useMemo(() => {
    return initDealLegs.some((dl) => isSingles(dl.totalUnsoldQuantity));
  }, [initDealLegs]);

  const tableRows = useMemo(
    () => makeStockRowData(initStockListings.stockDataMap),
    [initStockListings],
  );

  // this function is called by the PaginatedTable component when it needs more data to display.
  // we use it to fetch FSP values for the new deal legs that are being loaded.
  const loadMoreData = useCallback(
    async (offset: number) => {
      const newStockDataMap = await loadMoreStockDataMap(offset);

      if (newStockDataMap) {
        // calling `fetchFspForDealLegs` causes FspFetcher in useFspState() to immediately return
        // an updated fspValues state with entries for each new deal leg in 'loading' state.
        const dealLegIds = extractDealLegs(newStockDataMap).map(
          (dl) => dl.dealLegWithDbId.id,
        );
        fetchFspForDealLegs(dealLegIds);
        // update the stockDataMap with the new data.
        setStockDataMap(new Map([...stockDataMap, ...newStockDataMap]));
        // the combined effect will trigger a new `fspValuesMap` to be created with the new data.
      }
      return newStockDataMap && makeStockRowData(newStockDataMap);
    },
    [loadMoreStockDataMap, fetchFspForDealLegs, stockDataMap],
  );

  return (
    <PaginatedTable
      initialRows={tableRows}
      loadMoreData={loadMoreData}
      totalRowCount={initStockListings.totalProductCount}
      HeaderContent={
        <TableRow>
          <TableCell>Product</TableCell>
          <TableCell align="right">Due Date</TableCell>
          {displayQuantityColumn && (
            <TableCell align="right">Quantity</TableCell>
          )}
          <TableCell align="right">Forward Sale Price</TableCell>
          <TableCell align="right">Deposit Paid</TableCell>
        </TableRow>
      }
      BodyContent={
        <StockListingTableBody
          currency={currency}
          rows={tableRows}
          fspValuesMap={fspValuesMap}
        />
      }
    />
  );
};
